import React, { useEffect, useRef } from "react";
import SelectableCard from "../../components/SelectableCard";
import SkeletonTimesheet from "../../components/SkeletonTimesheet";
import TabContent from "../../components/TabContent";
import { EVENT_TYPES, logEvent } from "../../helpers/analytics-helper";
import { toPersianTimeString } from "../../helpers/date";
import {
  LogisticScheduledTaskPeriod,
  TimeSheet,
  TimeSheetItem,
  TransportationType,
} from "../../types/server";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Spacer,
  Typography,
  Divider,
  Stack,
} from "digiwash-components";
import { serverDateToJSDate, formatNumber } from "digiwash-core";
import { ActivityIndicator, Platform, ScrollView } from "react-native";

interface PropTypes {
  type: TransportationType;
  selectedAddressId?: number;
  disabledFor?: string;
  selectedTimesheetId: number;
  onSelect: (period: LogisticScheduledTaskPeriod) => void;
  onChangeDay?: (currentKey: string) => void;
  isInPerson?: boolean;
  timeesheets: {
    data: TimeSheet[];
    revalidate: (
      type: string,
      addressId: number,
      inPerson: boolean
    ) => Promise<TimeSheet[]>;
    isValidating: boolean;
    hasError: boolean;
  };
}

function available_days(timesheets) {
  return timesheets.map((sheet) => {
    let date = serverDateToJSDate(`${sheet.date} 12:00:00`);
    return {
      id: sheet.date,
      date,
      text: dayjs(date).format("dddd"),
    };
  });
}

// this is inner `time_sheet`
function available_times({
  date: initDate,
  time_sheets: timesheet,
}: {
  date: string;
  time_sheets: TimeSheetItem[];
}) {
  return timesheet.map((sheet) => {
    let time = `${toPersianTimeString(sheet.start)} تا ${toPersianTimeString(
      sheet.end
    )}`;
    let date: Date = serverDateToJSDate(`${initDate} ${sheet.start}`);
    let dateText = `${formatNumber(dayjs(date).format("D MMMM"))} ${time}`;
    let filled = sheet.filled;
    let disabled = sheet.disabled;
    return {
      id: sheet.id,
      text: time,
      date,
      dateText,
      filled,
      disabled,
    };
  });
}

CollectionSection.defaultProps = {
  type: "collection",
};
function checkSelectedDay(data, selectedId) {
  if (data && !!selectedId) {
    let timesheetIds = [];
    let output = data.find((item) => {
      item.time_sheets.map((i) => {
        timesheetIds.push(i.id);
      });
      return timesheetIds.includes(selectedId);
    });

    return output;
  }
}

function checkStartwithZero(num: string) {
  if (num.startsWith("0")) {
    let updatedNum = num.split("0");
    return updatedNum[1];
  } else {
    return num;
  }
}
function disableTimesheetForInPerson(endTime) {
  let now = new Date();
  let formattedNowWithTime = dayjs(now).format("HH:mm");
  let [nowHours, nowMinutes] = formattedNowWithTime.split(":");
  let [endHours, endMinutes] = endTime.split(":");

  if (
    Number(checkStartwithZero(nowHours)) > Number(checkStartwithZero(endHours))
  ) {
    return true;
  } else if (
    Number(checkStartwithZero(nowHours)) ===
      Number(checkStartwithZero(endHours)) &&
    Number(checkStartwithZero(nowMinutes)) >
      Number(checkStartwithZero(endMinutes))
  ) {
    return true;
  } else {
    return false;
  }
}

const webStyles =
  Platform.OS === "web"
    ? {
        timeButton: {
          minWidth: 64,
          textAlign: "center",
        },
        scrollViewContainer: {
          width: "100%",
        },
      }
    : {};

function CollectionSection(props: PropTypes) {
  let [currentKey, setCurrentKey] = React.useState(undefined);
  let isTabSelected = (key) => key === currentKey;

  let disabled = !props.selectedAddressId;

  let sliced_timesheets =
    props.timeesheets.data && props.timeesheets.data.slice(0, 7);
  let days = props.timeesheets.data ? available_days(sliced_timesheets) : [];
  let times = sliced_timesheets
    ? sliced_timesheets.map((sheet) => {
        return {
          id: sheet.date,
          date: sheet.date,
          data: available_times(sheet),
          originalData: sheet.time_sheets,
        };
      })
    : [];

  useEffect(() => {
    if (props.selectedAddressId) {
      props.timeesheets
        .revalidate(props.type, props.selectedAddressId, props.isInPerson)
        .then((revlidateDate) => {
          if (checkSelectedDay(revlidateDate, props.selectedTimesheetId)) {
            setCurrentKey(
              checkSelectedDay(revlidateDate, props.selectedTimesheetId).date
            );
          } else if (revlidateDate[0]) {
            setCurrentKey(revlidateDate[0].date);
          }
        });
    }
  }, [props.type, props.selectedAddressId]);

  let containerRef = useRef(null);

  return (
    <>
      {props.timeesheets.isValidating && <SkeletonTimesheet />}

      {!props.timeesheets.isValidating &&
        !disabled &&
        !props.timeesheets.hasError &&
        !props.disabledFor && (
          <Box style={{ width: "100%" }}>
            <Box
              style={{
                borderWidth: 2,
                borderColor: "#EBF8FA",
                borderStyle: "solid",
                position: "absolute",
                width: "100%",
                top: -65,
                direction: "rtl",
              }}
              key="ScrollContainer"
              bg="common.white"
              br="m"
              py="xm"
              px="m"
            >
              {props.timeesheets?.data?.length > 0 ? (
                <ScrollView
                  onContentSizeChange={() =>
                    Platform.OS === "android"
                      ? containerRef.current?.scrollToEnd({ animated: false })
                      : null
                  }
                  contentContainerStyle={{
                    flexGrow: 1,
                  }}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  key="Scrolling"
                  ref={containerRef}
                  style={{
                    ...{
                      flexGrow: 1,
                      direction: "rtl",
                    },
                    ...webStyles.scrollViewContainer,
                  }}
                >
                  <Box
                    style={{
                      flexGrow: 1,
                      flexDirection:
                        Platform.OS === "web" ? "row" : "row-reverse",
                    }}
                  >
                    {days.map((item) => {
                      let today = dayjs().format("YYYY-MM-DD");
                      let currentDay = dayjs(item.date).format("YYYY-MM-DD");
                      let text = item.text;
                      if (today === currentDay) {
                        text = "امروز";
                      }
                      return (
                        <>
                          <Button
                            fill={isTabSelected(item.id) ? "primary" : "white"}
                            onPress={() => {
                              logEvent(EVENT_TYPES.BUTTON_SELECT_TIMESHEET);
                              setCurrentKey(item.id);
                              if (props.onChangeDay) {
                                props.onChangeDay(currentKey);
                              }
                            }}
                          >
                            {`${text} \n ${formatNumber(
                              dayjs(item.date).format("D")
                            )}`}
                          </Button>
                        </>
                      );
                    })}
                  </Box>
                </ScrollView>
              ) : (
                <Stack distribution="center">
                  <Typography>بازه ای برای انتخاب وجود ندارد.</Typography>
                </Stack>
              )}
            </Box>

            <Divider size="xxxl" />
            {props.timeesheets.isValidating && (
              <>
                <Spacer size="m" />
                <ActivityIndicator color="#0000ff" />
              </>
            )}
            <TabContent currentKey={currentKey}>
              {times.map((time_slot) => {
                return (
                  <Box key={time_slot.id}>
                    {time_slot.data.map((item) => {
                      let today = dayjs().format("YYYY-MM-DD");
                      let currentDay = dayjs(item.date).format("YYYY-MM-DD");
                      let now = new Date();
                      let timesheet_item = time_slot.originalData.find(
                        (timesheet) => timesheet.id === item.id
                      );
                      let diff = dayjs(item.date).diff(now);
                      let passedTime = diff < 0;
                      let disabled = item.filled || passedTime;
                      let disabledText = null;
                      if (!passedTime && item.filled) {
                        disabledText = "ظرفیت تکمیل شده";
                      }
                      let disabledTimesheet;
                      if (props.isInPerson && today === currentDay) {
                        disabledTimesheet = disableTimesheetForInPerson(
                          timesheet_item.end
                        );
                      } else {
                        disabledTimesheet = disabled;
                      }
                      return (
                        <>
                          <Divider size="m" />
                          <SelectableCard
                            title={item.text}
                            selected={item.id === props.selectedTimesheetId}
                            onPress={() => {
                              if (item.id === props.selectedTimesheetId) {
                                props.onSelect(undefined);
                              } else {
                                props.onSelect({
                                  date: time_slot.date,
                                  end: timesheet_item.end,
                                  start: timesheet_item.start,
                                  id: timesheet_item.id,
                                });
                              }
                            }}
                            textColor={
                              disabledTimesheet ? "neutrals.300" : null
                            }
                            sub={disabledTimesheet ? disabledText : null}
                            subTextColor={
                              disabledTimesheet ? "orange.light" : null
                            }
                            disabled={disabledTimesheet}
                          />
                        </>
                      );
                    })}
                  </Box>
                );
              })}
            </TabContent>
          </Box>
        )}
      {props.disabledFor && (
        <Box py="l">
          <Typography ff="mainBold" alignment="center" color="neutrals.700">
            {props.disabledFor}
          </Typography>
        </Box>
      )}

      {props.timeesheets.hasError && (
        <Box py="l">
          <Typography ff="mainBold" alignment="center" color="neutrals.700">
            آدرس انتخاب شده تحت پوشش دیجی‌واش نیست.
          </Typography>
        </Box>
      )}
    </>
  );
}

export default CollectionSection;
